import { useEffect, useRef, useState } from 'react';
import { fetchEventoLookup, EventoLookupResult } from '../api/eventosApi';
import { notify } from '../shared/feedback/notify';

const DEBOUNCE_MS = 600;
const MIN_DIGITS = 4;

interface UseEventoLookupOptions {
  /** Habilita o lookup (ex: modal aberto e caminho com evento). */
  enabled: boolean;
  /** Número do evento digitado (0 = vazio). */
  eventNumber: number;
  /** Chamado uma única vez por busca concluída, com o resultado (evento local + veículos da Hinova). */
  onResult: (result: EventoLookupResult) => void;
  /**
   * at-96: chamado quando a consulta FALHA.
   *
   * O aviso em toast (abaixo) não basta para quem precisa distinguir "este evento não tem
   * veículo entregue" de "não consegui perguntar" — a primeira é uma afirmação de negócio, e a
   * regra da at-60 proíbe exibi-la sobre uma consulta que não respondeu. Opcional: o caminho
   * com evento não depende disso (ali o cadastro manual segue de qualquer forma).
   */
  onError?: () => void;
}

/**
 * Busca o evento a partir do número digitado, com debounce — a consulta só
 * dispara quando o usuário para de digitar, evitando uma chamada à API externa
 * (e um toast) por tecla.
 *
 * at-54: um único caminho. O lookup traz o evento local (se existir) e os
 * veículos do protocolo na Hinova, sem depender de o evento já estar cadastrado.
 */
export function useEventoLookup({ enabled, eventNumber, onResult, onError }: UseEventoLookupOptions) {
  const [isChecking, setIsChecking] = useState(false);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const protocolo = eventNumber === 0 ? '' : String(eventNumber);

    if (!enabled || protocolo.length < MIN_DIGITS) {
      setIsChecking(false);
      return;
    }

    let isCurrent = true;

    const timer = setTimeout(async () => {
      setIsChecking(true);
      try {
        const result = await fetchEventoLookup(protocolo);
        if (isCurrent) onResultRef.current(result);
      } catch {
        // A consulta é best-effort por decisão de negócio (at-54): o cadastro segue
        // manual. O que não pode é o usuário achar que o evento não existe quando,
        // na verdade, a consulta falhou. toastId fixo porque a busca é redisparada
        // a cada número digitado.
        if (isCurrent) {
          notify.warning(
            'Não foi possível consultar o evento. Confira o número e tente novamente; o preenchimento manual dos dados do veículo continua disponível.',
            { toastId: 'evento-lookup-indisponivel' }
          );
          onErrorRef.current?.();
        }
      } finally {
        if (isCurrent) setIsChecking(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [enabled, eventNumber]);

  return { isChecking };
}
